import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { type RunnerRow, useRunners } from '../lib/fleet';
import { BoxRequirements } from './BoxRequirements';
import { OnboardingCommand } from './OnboardingCommand';
import { OnboardingEnrollToken } from './OnboardingEnrollToken';
import { OnboardingHandshake } from './OnboardingHandshake';
import { SshEnrollPanel } from './SshEnrollPanel';
import { Button } from './ui/Button';

// The funnel's box step (epic #3629), laid out as the canvas ONBOARDING
// artboard: the command first, then the token it prompts for beside the
// handshake that watches the box come up. Container — page one of
// `GET /v1/runners` on the funnel's own 5s cadence (`useRunners` third
// argument), so a box's first heartbeat ticks its line in the same window the
// state poll moves the card.
//
// NO self-declared advance. The API's fact ladder settles `box_attached` off
// the heartbeat; this step only re-reads. A "Continue" here would be a caller
// asserting a platform-witnessed fact (`OnboardingEventWitness`), which is the
// door #2400 closed.
//
// The SSH-pull transport (the platform dials in and runs the join; no token
// ever reaches the browser) is a disclosure under the token: it is the second
// way in, not a peer of the command the step leads with. It is hidden by
// attribute, never unmounted, so a queued bootstrap survives a collapse.

/** The funnel poll cadence, so the roster and the state move together. */
export const BOX_STEP_POLL_MS = 5000;

export type OnboardingBoxStepProps = {
  readonly token: string | null;
};

export function OnboardingBoxStep(props: OnboardingBoxStepProps): JSX.Element {
  const runners = useRunners(() => null, props.token, BOX_STEP_POLL_MS);
  const [ssh, setSsh] = createSignal(false);

  // `undefined` while in flight or after a refusal — the `loadedRepos` rule.
  const rows = (): readonly RunnerRow[] | undefined =>
    runners.isLoading || runners.isError ? undefined : runners.data?.data;
  const refetch = (): void => void runners.refetch();

  return (
    <div class="onboarding-box">
      <BoxRequirements />
      <OnboardingCommand />
      <div class="onboarding-box__pair">
        <div class="onboarding-box__enroll">
          <OnboardingEnrollToken token={props.token} onMinted={refetch} />
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={ssh()}
            aria-controls="onboarding-box-ssh"
            onClick={() => setSsh((on) => !on)}
          >
            {t('onboarding.box_ssh_toggle')}
          </Button>
          <div id="onboarding-box-ssh" class="onboarding-box__ssh" hidden={!ssh()}>
            <SshEnrollPanel
              open={ssh()}
              token={props.token}
              onQueued={refetch}
              onClose={() => setSsh(false)}
            />
          </div>
        </div>
        <OnboardingHandshake
          runners={rows()}
          failed={runners.isError}
          error={runners.error}
          onRetry={refetch}
        />
      </div>
    </div>
  );
}
